'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BsBrushFill,
  BsDashCircle,
  BsIncognito,
  BsLightningChargeFill,
} from 'react-icons/bs'
import {
  FaArrowUp,
  FaChevronRight,
  FaFemale,
  FaFire,
  FaFlask,
  FaLongArrowAltRight,
  FaMagic,
  FaMobile,
  FaUsers,
  FaVideo,
} from 'react-icons/fa'
import { TbCards } from 'react-icons/tb'
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
import Card from '../../component/Card'
import { staticImages } from '../../config/staticImages'
import { useAuth } from '../../contexts/AuthContext'
import { ImageContext } from '../../contexts/ImageContext'
import {
  activeButtonStyle,
  inactiveButtonStyle,
  isSafeCountry,
} from '../../helpers/helpers'

const conversation = [
  { text: 'Hi Jenny, how are ya? \u{1F608}', type: 'sender' }, // 😈
  { text: "Heyyyy, I'm great! How are you? \u{1F618}", type: 'recipient' }, // 😘
  {
    text: 'Meh, maybe a selfie will brighten my day \u{1F60D}\u{1F60D}',
    type: 'sender',
  }, // 😍😍
  { text: 'Aw okay, hows this honey? \u{1F352}\u{1F4A6}', type: 'recipient' }, // 🍒💦
  { text: 'photo', type: 'recipient' },
]

const NsfwAiLandingBottomHalf = ({
  setSafeWarningOpen,
  upgradeModal,
  setUpgradeModal,
}) => {
  const { t } = useTranslation()

  const { user, safe } = useAuth()
  const router = useRouter()
  const {
    activeButton,
    model,
    images,
    setImages,
    filteredImages,
    setFilteredImages,
  } = useContext(ImageContext)

  const [hasStartedAnimation, setHasStartedAnimation] = useState(false)
  const [messages, setMessages] = useState([])

  const [isQuestion1Open, setIsQuestion1Open] = useState(true)
  const [isQuestion2Open, setIsQuestion2Open] = useState(true)
  const [isQuestion4Open, setIsQuestion4Open] = useState(true)
  const [isQuestion5Open, setIsQuestion5Open] = useState(true)

  useEffect(() => {
    setFilteredImages(staticImages)
  }, [])

  useEffect(() => {
    const chatContainer = document.querySelector('.chat-container')

    const observer = new IntersectionObserver(
      (entries) => {
        // Check if chat container is intersecting (visible) and animation hasn't started yet
        if (entries[0].isIntersecting && !hasStartedAnimation) {
          setHasStartedAnimation(true) // Prevent further animation triggers

          // Your existing logic to start sending messages
          if (user || conversation.length === 0) {
            return
          }

          setMessages([conversation[0]]) // Initialize by setting the first message immediately.
          let i = 1 // Start from the second message.

          const sendNextMessage = () => {
            if (i < conversation.length) {
              setMessages((msgs) => [...msgs, conversation[i]])
              i++
              // After each message, adjust the timer for the next one.
              const nextDelay = i === 1 ? 2000 : Math.random() * 2000 + 1000 // 2 seconds for the second, random 1-3 for the rest.
              setTimeout(sendNextMessage, nextDelay)
            }
          }

          // Schedule the second message. This uses a fixed delay as per the initial requirement.
          if (conversation.length > 1) {
            setTimeout(sendNextMessage, 2000) // Start with a fixed delay for the second message.
          }
        }
      },
      {
        rootMargin: '0px',
        threshold: 0.1, // Trigger when 10% of the chat container is visible
      },
    )

    // Start observing the chat container
    if (chatContainer) {
      observer.observe(chatContainer)
    }

    // Cleanup observer on component unmount
    return () => {
      observer.disconnect()
    }
  }, [user, hasStartedAnimation, conversation]) // Ensure all relevant dependencies are listed

  return (
    <div className="py-3 px-0 lg:px-8 mx-auto">
      <section className="container mx-auto md:px-4 pb-8">
        <div className="mb-12 flex flex-col mx-auto items-center justify-center text-center">
          <h2 className="text-white font-semibold text-5xl flex items-center justify-center">
            {t('home_madeWithTitle')}
          </h2>
          <h3 className="text-gray-300 mt-2 md:w-1/3">
            {t('nsfwai_madeWithSubtitle')}
          </h3>
        </div>

        <div className="flex items-center justify-between mb-4 ">
          <div className="flex flex-col">
            <div className="inline-flex items-center md:space-x-2 space-x-1 flex-wrap">
              <button
                className={`${
                  activeButton === 'Top'
                    ? activeButtonStyle
                    : inactiveButtonStyle
                } font-semibold md:w-32 w-11 text-center md:py-2 py-3 rounded-full flex items-center justify-center mb-1`}
                aria-label="Top images"
                onClick={() => {
                  router.push('/signup')
                }}
              >
                <FaArrowUp className="md:mr-1" />
                <p className="md:block hidden text-sm">{t('top')}</p>
              </button>

              <button
                className={`${
                  activeButton === 'Feed'
                    ? activeButtonStyle
                    : inactiveButtonStyle
                } font-semibold md:w-32 w-11 text-center md:py-2 py-3 rounded-full flex items-center justify-center mb-1`}
                aria-label="Newest images"
                onClick={() => {
                  router.push('/signup')
                }}
              >
                <FaFire className="md:mr-1" />
                <p className="md:block hidden text-sm">{t('newest')}</p>
              </button>

              {/* Video is Safe-Mode-gated, and safe-country visitors can never
                  turn Safe Mode off — hide the tab instead of pointing them at a
                  Settings toggle that isn't rendered for them. */}
              {!isSafeCountry() && (
                <button
                  className={`${
                    activeButton === 'Video'
                      ? activeButtonStyle
                      : inactiveButtonStyle
                  } font-semibold md:w-32 w-11 text-center md:py-2 py-3 rounded-full flex items-center justify-center mb-1`}
                  aria-label="Videos"
                  onClick={() => {
                    if (safe) {
                      setSafeWarningOpen(true)
                      return
                    }

                    router.push('/signup')
                  }}
                >
                  <FaVideo className="md:mr-1" />
                  <p className="md:block hidden text-sm">{t('video')}</p>
                </button>
              )}

              <button
                className={`${
                  activeButton === 'Following'
                    ? activeButtonStyle
                    : inactiveButtonStyle
                }  font-semibold md:w-32 w-11 text-center md:py-2 py-3 rounded-full flex items-center justify-center mb-1`}
                aria-label="Following"
                onClick={() => {
                  router.push('/signup')
                }}
              >
                <FaUsers className="md:mr-1" />
                <p className="md:block hidden text-sm">{t('following')}</p>
              </button>

              <div className="hidden sm:block border-r border-gray-500 h-8 self-center pl-2"></div>

              <div className="relative inline-flex md:pl-2">
                <svg
                  className="w-2 h-2 absolute top-0 right-0 m-4 pointer-events-none"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 412 232"
                >
                  <path
                    d="M206 232l206-232H0z"
                    fill="#fff"
                    fillRule="evenodd"
                  ></path>
                </svg>
                <select
                  onChange={(e) => {
                    router.push('/signup')
                  }}
                  aria-label="Style"
                  name="Style"
                  className={`rounded-full font-semibold w-40 text-center h-9 px-8 hover:border-gray-400 focus:outline-none appearance-none text-sm cursor-pointer ${
                    model === 'all' ? inactiveButtonStyle : activeButtonStyle
                  }`}
                >
                  <option value="all">{t('allStyles')}</option>
                  <option value="Real">{t('real')}</option>
                  <option value="Hyperreal"> {t('hyperreal')}</option>
                  <option value="Anime">{t('anime')}</option>
                  <option value="Anime XL">{t('anime xl')}</option>
                  <option value="Cinematic XL">{t('cinematic xl')}</option>
                  <option value="Hardcore XL">{t('hardcore xl')}</option>

                  <option value="Hyperanime">{t('hyperanime')}</option>
                  <option value="K-Pop">{t('k-pop')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <>
          {filteredImages && filteredImages.length > 0 ? (
            <>
              <ResponsiveMasonry
                columnsCountBreakPoints={{
                  350: 1,
                  750: 2,
                  1000: 3,
                  1750: 4,
                }}
              >
                <Masonry gutter="0.3rem">
                  {filteredImages
                    .filter((image) => !!image)
                    .map((image) => (
                      <div
                        key={image.id}
                        className="ring-1 ring-[var(--secondary-bg-color)] overflow-hidden rounded-xl"
                      >
                        <Card
                          {...image}
                          popImage={(imageId) => {
                            setImages(
                              images.filter((img) => img.id !== imageId),
                            )
                          }}
                          imageData={image}
                          upgradeModal={upgradeModal}
                          setUpgradeModal={setUpgradeModal}
                          nsfw={true}
                          shouldLazyLoad={true}
                        />
                      </div>
                    ))}
                </Masonry>
              </ResponsiveMasonry>
            </>
          ) : (
            <>
              <p className="text-white text-center my-12">
                {t('home_noImagesFound')}
              </p>
            </>
          )}

          <Link href="/signup">
            <div className="flex items-center justify-center px-5 w-72 max-w-full py-3 mb-36 mt-10 bg-[var(--secondary-bg-color)] text-white rounded-3xl focus:outline-none ml-auto mr-auto font-semibold shadow-lg md:text-lg text-center transition-transform duration-200 hover:scale-[1.03]">
              {t('signUpToViewMore')} <FaLongArrowAltRight className="ml-2" />
            </div>
          </Link>
        </>
        <>
          <div className="my-12 mx-auto md:w-2/3">
            <div className="p-2">
              <Link href="/signup">
                <img
                  loading="lazy"
                  src="/assets/landing_create.webp"
                  className="rounded-xl"
                  alt="Nude blonde in spaceship"
                />
              </Link>{' '}
            </div>
            <div className="p-4 my-auto">
              <h2 className="text-5xl font-semibold tracking-tight text-white mb-4 flex items-center">
                {t('nsfwai_createTitle')}
              </h2>
              <h3 className="tracking-tight text-gray-300 text-lg">
                {t('nsfwai_createBody')}
              </h3>
              <Link href="/signup">
                <button
                  className="text-[#786FFF] text-xl my-3 flex items-center"
                  aria-label="Get started"
                >
                  {t('getStarted')} <FaChevronRight className="ml-2" />
                </button>
              </Link>
            </div>
          </div>
          <div className="my-24 md:flex">
            <div className="md:w-1/2 p-2 my-auto">
              <div className="justify-end flex">
                <Link href="/signup">
                  <button
                    className="text-[#786FFF] text-xl my-3 flex items-center"
                    aria-label="Get started"
                  >
                    {t('getStarted')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>
            </div>
          </div>
          <div className="my-24 md:flex">
            <div className="md:w-1/2 p-2 my-auto">
              <h2 className="text-4xl font-semibold tracking-tight text-white mb-4 flex items-center">
                <FaMagic className="mr-2" />
                {t('nsfwai_cloneArtTitle')}
              </h2>
              <h3 className="tracking-tight text-gray-300">
                {t('nsfwai_cloneArtBody')}
              </h3>
              <Link href="/signup">
                <button
                  className="text-[#786FFF] text-xl my-3 flex items-center"
                  aria-label="Get started"
                >
                  {t('getStarted')} <FaChevronRight className="ml-2" />
                </button>
              </Link>
            </div>
            <div className="md:w-1/2 p-2">
              {' '}
              <Link href="/signup">
                <img
                  loading="lazy"
                  src="/assets/landing_clone.webp"
                  className="rounded-xl"
                  alt="Image cloning example"
                />
              </Link>{' '}
            </div>
          </div>
          <div className="my-24 md:flex">
            <div className="md:w-1/2 p-2">
              <Link href="/signup">
                <img
                  loading="lazy"
                  src="/assets/landing_pose.webp"
                  className="rounded-xl"
                  alt="Pose prompt example"
                />
              </Link>
            </div>
            <div className="md:w-1/2 p-2 my-auto">
              <h2 className="text-4xl font-semibold tracking-tight text-white mb-4 flex items-center justify-end">
                <FaFemale className="mr-2" />
                {t('NsfwAiPosing')}
              </h2>
              <h3 className="tracking-tight text-gray-300 text-right">
                {t('nsfwai_posingBody')}
              </h3>
              <div className="justify-end flex">
                <Link href="/signup">
                  <button
                    className="text-[#786FFF] text-xl my-3 flex items-center"
                    aria-label="Get started"
                  >
                    {t('getStarted')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>
            </div>
          </div>
          <div className="my-24 md:flex">
            <div className="md:w-1/2 p-2 my-auto">
              <h2 className="text-4xl font-semibold tracking-tight text-white mb-4 flex items-center">
                <BsBrushFill className="mr-2" />
                {t('anyNsfwAiArtStyle')}
              </h2>
              <h3 className="tracking-tight text-gray-300">
                {t('nsfwai_styleBody')}
              </h3>
              <Link href="/signup">
                <button
                  className="text-[#786FFF] text-xl my-3 flex items-center"
                  aria-label="Get started"
                >
                  {t('getStarted')} <FaChevronRight className="ml-2" />
                </button>
              </Link>
            </div>
            <div className="md:w-1/2 p-2">
              {' '}
              <Link href="/signup">
                <img
                  loading="lazy"
                  src="/assets/landing_styles.webp"
                  className="rounded-xl"
                  alt="Image styles showcase"
                />
              </Link>{' '}
            </div>
          </div>
          <>
            <div className="flex-grow my-6"></div>

            <div className="mb-16 mx-auto md:w-2/3">
              <div className="chat-container bg-[var(--middle-bg-color)] rounded-xl">
                {messages.map(
                  (msg, index) =>
                    msg && (
                      <div
                        key={index}
                        className={`message-row ${
                          msg.type === 'recipient'
                            ? 'recipient-row'
                            : 'sender-row'
                        }`}
                      >
                        <img
                          loading="lazy"
                          src={
                            msg.type === 'recipient'
                              ? '/assets/profiles/girl_3_pic_2.webp'
                              : 'https://gravatar.com/avatar/3246a02ac0a853fbc05c6d85753ac06a?s=200&d=mp&r=x'
                          }
                          alt={msg.type === 'sender' ? 'Sender' : 'Recipient'}
                          className={`profile-image ${
                            msg.type === 'recipient'
                              ? 'recipient-image'
                              : 'sender-image'
                          }`}
                        />
                        {msg.text.toLowerCase() === 'photo' ? (
                          <img
                            loading="lazy"
                            src="/assets/profiles/girl_3_pic_3.webp"
                            alt="Shared Photo"
                            className="shared-photo opacity-0 transform scale-90 transition-all duration-500 ease-in-out md:w-1/3 w-2/3"
                            onLoad={(e) => {
                              e.currentTarget.classList.add(
                                'opacity-100',
                                'scale-100',
                              )
                            }}
                          />
                        ) : (
                          <div
                            className={
                              msg.type === 'sender'
                                ? 'sender-bubble'
                                : 'chat-bubble'
                            }
                          >
                            {msg.text}
                          </div>
                        )}
                      </div>
                    ),
                )}
              </div>

              <div className="p-4 my-auto">
                <h2 className="text-4xl font-semibold tracking-tight text-white mb-4 flex items-center">
                  {t('nsfwai_chatTitle')}
                </h2>
                <h3 className="tracking-tight text-gray-300">
                  {t('nsfwai_chatIntroPre')}{' '}
                  <a
                    href="https://character.ai"
                    rel="nofollow"
                    target="_blank"
                    className="underline"
                  >
                    Character AI
                  </a>{' '}
                  {t('nsfwai_chatIntroPost')}
                </h3>
                <Link href="/chat">
                  <button
                    className="text-[#786FFF] text-xl my-3 flex items-center"
                    aria-label="Start Chatting NSFW"
                  >
                    {t('startChattingNsfw')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>
            </div>

            <div className="my-24 md:flex">
              <div className="md:w-1/2 p-2 my-auto">
                <h2 className="text-4xl font-semibold tracking-tight text-white mb-4 flex items-center">
                  <TbCards className="mr-2" />
                  {t('nsfwai_charactersTitle')}
                </h2>
                <h3 className="tracking-tight text-gray-300">
                  {t('nsfwai_charactersBody')}
                </h3>
                <Link href="/chat">
                  <button
                    className="text-[#786FFF] text-xl my-3 flex items-center"
                    aria-label="Start Chatting"
                  >
                    {t('startChatting')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>
              <div className="md:w-1/2 p-2">
                {' '}
                <Link href="/chat">
                  <img
                    loading="lazy"
                    src="/assets/chatLanding/chat2.webp"
                    className="rounded-xl"
                    alt="Chat with AI"
                  />
                </Link>{' '}
              </div>
            </div>

            <div className="my-24 md:flex">
              <div className="md:w-1/2 p-2">
                <Link href="/chat">
                  <img
                    loading="lazy"
                    src="/assets/chatLanding/chat3.webp"
                    className="rounded-xl"
                    alt="Chat with AI"
                  />
                </Link>
              </div>

              <div className="md:w-1/2 p-2 my-auto">
                <h2 className="text-4xl font-semibold tracking-tight text-white mb-4 flex items-center justify-end">
                  <FaFlask className="mr-2" />
                  {t('nsfwai_idealTitle')}
                </h2>
                <h3 className="tracking-tight text-gray-300 text-right">
                  {t('nsfwai_idealBody')}
                </h3>
                <div className="justify-end flex">
                  <Link href="/chat">
                    <button
                      className="text-[#786FFF] text-xl my-3 flex items-center"
                      aria-label="Start Chatting"
                    >
                      {t('startChatting')} <FaChevronRight className="ml-2" />
                    </button>
                  </Link>
                </div>
              </div>
            </div>

            <div className="flex-grow  my-6"></div>

            <div className="my-24">
              <h2 className="text-white md:text-2xl font-semibold text-center mb-1">
                {t('nsfwai_realisticTitle')}
              </h2>
              <p className="text-gray-300 text-center mb-8 md:w-1/2 mx-auto">
                {t('nsfwai_realisticBody')}
              </p>
              <Link href="/chat">
                <div className="flex items-center justify-center px-5 w-72 max-w-full py-3 my-4 bg-[var(--secondary-bg-color)] text-white rounded-full focus:outline-none ml-auto mr-auto font-semibold shadow-lg md:text-lg text-center transition-transform duration-200 hover:scale-[1.03]">
                  {t('startChattingNsfw')}
                  <FaLongArrowAltRight className="ml-2" />
                </div>
              </Link>
            </div>
          </>
          <div className="my-24 md:flex">
            <div className="md:w-1/2 p-2 my-auto">
              <h2 className="text-4xl font-semibold tracking-tight text-white mb-4 flex items-center">
                <FaMagic className="mr-2" /> {t('nsfwai_hentaiTitle')}
              </h2>
              <h3 className="tracking-tight text-gray-300">
                {t('nsfwai_hentaiBody')}
              </h3>
              <Link href="/signup">
                <button
                  className="text-[#786FFF] text-xl my-3 flex items-center"
                  aria-label="Get started"
                >
                  {t('getStarted')} <FaChevronRight className="ml-2" />
                </button>
              </Link>
            </div>
            <div className="md:w-1/2 p-2">
              {' '}
              <Link href="/signup">
                <img
                  loading="lazy"
                  src="/assets/createanime.webp"
                  className="rounded-xl"
                  alt="AI Anime Images"
                />
              </Link>{' '}
            </div>
          </div>
          <div className="my-24 md:flex">
            <div className="md:w-1/2 p-2 my-auto">
              <h2 className="text-4xl font-semibold tracking-tight text-white mb-4 flex items-center">
                <FaMagic className="mr-2" />
                {t('nsfwai_cloneHentaiTitle')}
              </h2>
              <h3 className="tracking-tight text-gray-300">
                {t('nsfwai_cloneHentaiBody')}
              </h3>
              <Link href="/signup">
                <button
                  className="text-[#786FFF] text-xl my-3 flex items-center"
                  aria-label="Get started"
                >
                  {t('getStarted')} <FaChevronRight className="ml-2" />
                </button>
              </Link>
            </div>
            <div className="md:w-1/2 p-2">
              {' '}
              <Link href="/signup">
                <img
                  loading="lazy"
                  src="/assets/cloneanime.webp"
                  className="rounded-xl"
                  alt="Clone images with AI"
                />
              </Link>{' '}
            </div>
          </div>
          <div className="bg-[var(--middle-bg-color)] rounded-3xl px-12 py-3">
            <div className="my-24 md:flex">
              <div className="md:w-1/2 p-2 my-auto">
                <h2 className="text-4xl font-semibold tracking-tight text-white mb-4 flex items-center">
                  <BsIncognito className="mr-2" /> {t('nsfwai_privateTitle')}
                </h2>
                <h3 className="tracking-tight text-gray-300">
                  {t('nsfwai_privateBody')}
                </h3>
                <Link href="/signup">
                  <button
                    className="text-[#786FFF] my-3 flex items-center"
                    aria-label="Get started"
                  >
                    {t('getStarted')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>

              <div className="md:w-1/2 p-2 my-auto">
                <h2 className="text-4xl font-semibold tracking-tight text-white mb-4 flex items-center">
                  <BsDashCircle className="mr-2" />
                  {t('nsfwai_uncensoredTitle')}
                </h2>
                <h3 className="tracking-tight text-gray-300">
                  {t('nsfwai_uncensoredBody')}
                </h3>
                <Link href="/signup">
                  <button
                    className="text-[#786FFF] my-3 flex items-center"
                    aria-label="Get started"
                  >
                    {t('getStarted')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>
            </div>
            <div className="my-24 md:flex">
              <div className="md:w-1/2 p-2 my-auto">
                <h2 className="text-4xl font-semibold tracking-tight text-white mb-4 flex items-center">
                  <FaMobile className="mr-2" /> {t('nsfwai_mobileTitle')}
                </h2>
                <h3 className="tracking-tight text-gray-300">
                  {t('nsfwai_mobileBody')}
                </h3>
                <Link href="/signup">
                  <button
                    className="text-[#786FFF] my-3 flex items-center"
                    aria-label="Get started"
                  >
                    {t('getStarted')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>

              <div className="md:w-1/2 p-2 my-auto">
                <h2 className="text-4xl font-semibold tracking-tight text-white mb-4 flex items-center">
                  <BsLightningChargeFill className="mr-2" />{' '}
                  {t('nsfwai_fastestTitle')}
                </h2>
                <h3 className="tracking-tight text-gray-300">
                  {t('nsfwai_fastestBody')}
                </h3>
                <Link href="/signup">
                  <button
                    className="text-[#786FFF] my-3 flex items-center"
                    aria-label="Get started"
                  >
                    {t('getStarted')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>
            </div>
          </div>
          <div className="px-6 lg:px-8 max-w-5xl mx-auto py-4 text-white mt-14 ">
            <h2 className="text-3xl font-semibold tracking-tight mb-2 text-center">
              {t('promptchanFaq')}
            </h2>
            <h3 className="tracking-tight text-gray-300 text-center">
              {t('nsfwai_faqSubtitle')}
            </h3>

            <div className="space-y-4 pt-6">
              <div className="border-b border-gray-800">
                <button
                  onClick={() => setIsQuestion1Open(!isQuestion1Open)}
                  aria-label="What is an NSFW AI Image Generator?"
                  className="flex justify-between items-center w-full text-left text-xl font-semibold p-4"
                >
                  <span>{t('whatIsAnNsfw')}</span>
                  <span>{isQuestion1Open ? '-' : '+'}</span>
                </button>
                <p
                  className={`pl-4 pr-10 pb-4 ${
                    isQuestion1Open ? 'block' : 'hidden'
                  }`}
                >
                  {t('nsfwai_faqA1')}
                </p>
              </div>
              <div className="border-b border-gray-800">
                <button
                  onClick={() => setIsQuestion5Open(!isQuestion5Open)}
                  aria-label="Is Promptchan AI free?"
                  className="flex justify-between items-center w-full text-left text-xl font-semibold p-4"
                >
                  <span>{t('isPromptchanFree')}</span>
                  <span>{isQuestion5Open ? '-' : '+'}</span>
                </button>
                <p
                  className={`pl-4 pr-10 pb-4 ${
                    isQuestion5Open ? 'block' : 'hidden'
                  }`}
                >
                  {t('nsfwai_faqA2')}
                </p>
              </div>
              <div className="border-b border-gray-800">
                <button
                  onClick={() => setIsQuestion2Open(!isQuestion2Open)}
                  aria-label="Is Promptchan Private?"
                  className="flex justify-between items-center w-full text-left text-xl font-semibold p-4"
                >
                  <span>{t('isPromptchanSafe')}</span>
                  <span>{isQuestion1Open ? '-' : '+'}</span>
                </button>
                <p
                  className={`pl-4 pr-10 pb-4  ${
                    isQuestion2Open ? 'block' : 'hidden'
                  }`}
                >
                  {t('nsfwai_faqA3')}
                </p>
              </div>
              <div className="border-b border-gray-800">
                <button
                  onClick={() => setIsQuestion4Open(!isQuestion4Open)}
                  aria-label="Can Promptchan be used commercially?"
                  className="flex justify-between items-center w-full text-left text-xl font-semibold p-4"
                >
                  <span>{t('canPromptchanBeUsed')}</span>
                  <span>{isQuestion4Open ? '-' : '+'}</span>
                </button>
                <p
                  className={`pl-4 pr-10 pb-4  ${
                    isQuestion4Open ? 'block' : 'hidden'
                  }`}
                >
                  {t('nsfwai_faqA4')}
                </p>
              </div>
            </div>

            <h4 className="text-center my-12">
              {t('haveMoreQuestionsAbout')} {t('visitOur')}{' '}
              <a
                className="underline"
                href="https://discord.gg/cZuJFFb34e"
                target="_blank"
                rel="noreferrer"
              >
                {t('discordCommunity')}
              </a>{' '}
              {t('toAskForHelp')}
            </h4>
          </div>{' '}
          <div
            style={{
              backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.2) 10%, rgba(11, 34, 99, 0.8)), url(/assets/header.webp)`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
            }}
            className="h-60 flex flex-col items-center justify-center rounded-xl mt-20"
          >
            <h4 className="text-white md:text-2xl font-semibold text-center">
              {t('tryOut')}{' '}
            </h4>
            <Link href="/signup">
              <div className="flex items-center justify-center px-5 w-72 max-w-full py-3 my-4 bg-white text-[var(--main-bg-color)] rounded-full focus:outline-none ml-auto mr-auto font-semibold shadow-lg md:text-lg text-center transition-transform duration-200 hover:scale-[1.05]">
                {t('tryItNow')}
                <FaLongArrowAltRight className="ml-2" />
              </div>
            </Link>
          </div>
        </>
      </section>
    </div>
  )
}

export default NsfwAiLandingBottomHalf
